// The token grammar for title and text widgets, kept in one pure module so the browser,
// the PDF Lambda and the node test runner share it (the arrangement of
// `Processing/countSpec.mjs`). `resolveTemplate.ts` re-exports it with types.
//
// Two token families share one regex: `{CurrentDate}` and its siblings, and the slicer
// tokens `{Slicer.KEY.Variable±N:format}`. The backend compiles the same regex in
// `api/Sandbox/title_tokens.py` and validates a save with the same rules.

import dayjs from "dayjs";

export const TOKEN_RE =
  /\{\s*(?:(Slicer)\.([A-Za-z0-9]{1,4})\.)?(\w+)\s*(?:([+-])\s*(\d+))?\s*(?::([^}]*))?\s*\}/gi;

/** Today's brace scan: what the token regex does not match but a coach may have meant. */
export const BRACE_RE = /\{(\w+)/g;

export const CURRENT_VARIABLES = [
  { key: "CurrentDate", source: "client", kind: "date", defaultFormat: "YYYY-MM-DD", offsetUnit: "day" },
  { key: "CurrentMonth", source: "client", kind: "month", defaultFormat: "MMMM", offsetUnit: "month" },
  { key: "CurrentYear", source: "client", kind: "year", defaultFormat: "YYYY", offsetUnit: "year" },
];

// An unprefixed variable keeps its exact spelling, so the lookup is case-sensitive.
const CURRENT_BY_NAME = new Map(CURRENT_VARIABLES.map((v) => [v.key, v]));

/** Each slicer variable and the controller kinds that offer it. */
export const SLICER_VARIABLES = {
  StartDate: ["date", "single_date"],
  EndDate: ["date", "single_date"],
  DateRange: ["date"],
  DayFilter: ["date"],
  Subjects: ["subject", "single_player"],
  SubjectCount: ["subject", "single_player"],
  Athlete: ["subject", "single_player"],
  Drills: ["subject"],
};

/** The variables each kind offers, in the order the menu and the help popover list them. */
export const SLICER_VARIABLE_ORDER = {
  date: ["StartDate", "EndDate", "DateRange", "DayFilter"],
  subject: ["Subjects", "SubjectCount", "Athlete", "Drills"],
  single_date: ["StartDate", "EndDate"],
  single_player: ["Subjects", "SubjectCount", "Athlete"],
};

export const DATE_VARIABLES = ["StartDate", "EndDate"];
export const LIST_VARIABLES = ["Subjects", "Drills"];
export const DEFAULT_DATE_FORMAT = "YYYY-MM-DD";

const CANONICAL_BY_LOWER = new Map(
  Object.keys(SLICER_VARIABLES).map((name) => [name.toLowerCase(), name]),
);

/** The canonical spelling of a slicer variable, looked up without case; null when unknown. */
export function canonicalVariable(name) {
  if (typeof name !== "string") return null;
  return CANONICAL_BY_LOWER.get(name.toLowerCase()) ?? null;
}

/** The ordered union of the variables these controls offer. */
export function variablesForControls(controls) {
  const out = [];
  for (const kind of Object.keys(SLICER_VARIABLE_ORDER)) {
    if (!controls?.includes(kind)) continue;
    for (const variable of SLICER_VARIABLE_ORDER[kind]) {
      if (!out.includes(variable)) out.push(variable);
    }
  }
  return out;
}

/** `date` or `subject`; the legacy kinds read as their modern kind. */
export function kindWord(controls) {
  return controls?.some((c) => c === "date" || c === "single_date") ? "date" : "subject";
}

// TipTap stores a non-breaking space as the six-character entity, not U+00A0.
const NBSP_ENTITY = /&nbsp;/g;

/**
 * Every well-formed token in the text, in order. A slicer token carries its key in upper
 * case and its variable in canonical spelling when the name is known; an unprefixed
 * token keeps the variable as typed.
 */
export function parseTokens(text) {
  const tokens = [];
  if (typeof text !== "string") return tokens;
  for (const match of text.matchAll(TOKEN_RE)) {
    const [raw, prefix, key, word, sign, amount, format] = match;
    const prefixed = prefix != null;
    const cleanFormat = format == null ? null : format.replace(NBSP_ENTITY, " ").trim();
    tokens.push({
      raw,
      start: match.index,
      end: match.index + raw.length,
      prefixed,
      key: prefixed ? key.toUpperCase() : null,
      variable: prefixed ? canonicalVariable(word) ?? word : word,
      sign: sign ?? null,
      amount: amount == null ? null : parseInt(amount, 10),
      format: cleanFormat ? cleanFormat : null,
    });
  }
  return tokens;
}

/** The upper-cased keys the slicer tokens in this text name. */
export function slicerKeysInTemplate(text) {
  const keys = new Set();
  for (const token of parseTokens(text)) {
    if (token.prefixed) keys.add(token.key);
  }
  return keys;
}

// The spec defines DDD (short weekday) and DDDD (full weekday) which differ from dayjs
// (ddd/dddd). All other tokens are identical between spec and dayjs.
function toDayjsFormat(specFormat) {
  return specFormat.replace(/DDDD/g, "dddd").replace(/DDD/g, "ddd");
}

// Longest-first so greedy matching doesn't consume e.g. "D" before "DD".
const ALLOWED_TOKENS = ["DDDD", "DDD", "DD", "D", "MMMM", "MMM", "MM", "M", "YYYY", "YY"];

function isValidSpecFormat(format) {
  let i = 0;
  while (i < format.length) {
    if (/[A-Za-z]/.test(format[i])) {
      const matched = ALLOWED_TOKENS.find((t) => format.startsWith(t, i));
      if (!matched) return false;
      i += matched.length;
    } else {
      i++;
    }
  }
  return true;
}

function unsupportedFormat(format) {
  return `Unsupported format "${format}" — only use: YYYY YY M MM MMM MMMM D DD DDD DDDD`;
}

function signedOffset(token) {
  if (token.amount == null) return 0;
  return token.sign === "-" ? -token.amount : token.amount;
}

const HTML_ESCAPES = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };

function escapeHtml(text) {
  return text.replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);
}

/** A `{CurrentDate}` family token; null keeps the literal. */
function resolveCurrentToken(token, now, warnings) {
  const variable = CURRENT_BY_NAME.get(token.variable);
  if (!variable) {
    warnings.push(`Unknown variable: ${token.variable}`);
    return null;
  }
  const format = token.format ?? variable.defaultFormat;
  if (!isValidSpecFormat(format)) {
    warnings.push(unsupportedFormat(format));
    return null;
  }
  const base = dayjs(now);
  const offset = signedOffset(token);
  const date = offset !== 0 ? base.add(offset, variable.offsetUnit) : base;
  return date.format(toDayjsFormat(format));
}

function formatDateValue(token, value, warnings) {
  const format = token.format ?? DEFAULT_DATE_FORMAT;
  if (!isValidSpecFormat(format)) {
    warnings.push(unsupportedFormat(format));
    return null;
  }
  const base = dayjs(value);
  if (!base.isValid()) return null;
  const offset = signedOffset(token);
  const date = offset !== 0 ? base.add(offset, "day") : base;
  return date.format(toDayjsFormat(format));
}

// A list value is one comma-joined string on both sides of the wire, so the cap splits
// on the separator `slicerVariables.mjs` joins with.
const LIST_SEPARATOR = ", ";

function capList(token, value, warnings) {
  if (token.sign) warnings.push(`${token.variable} takes no offset`);
  if (token.format == null) return value;
  const cap = /^\d+$/.test(token.format) ? parseInt(token.format, 10) : 0;
  if (cap < 1) {
    warnings.push(`${token.variable} takes a number cap, not "${token.format}"`);
    return value;
  }
  const items = value === "" ? [] : value.split(LIST_SEPARATOR);
  if (items.length <= cap) return value;
  return `${items.slice(0, cap).join(LIST_SEPARATOR)} +${items.length - cap} more`;
}

/** A `{Slicer.KEY.Variable}` token; null keeps the literal. */
function resolveSlicerToken(token, values, warnings) {
  // No board context at all (today's one-argument callers): the token stays literal.
  if (values === undefined) return null;
  const slicer = values?.[token.key];
  if (!slicer) {
    warnings.push(`No slicer has key ${token.key}`);
    return null;
  }
  if (!Object.prototype.hasOwnProperty.call(SLICER_VARIABLES, token.variable)) {
    warnings.push(`Unknown slicer variable: ${token.variable}`);
    return null;
  }
  const offered = slicer.values ?? {};
  if (!Object.prototype.hasOwnProperty.call(offered, token.variable)) {
    warnings.push(`${token.key} is a ${slicer.kind} slicer and has no ${token.variable}`);
    return null;
  }
  const value = offered[token.variable];
  if (value == null) return null;
  if (DATE_VARIABLES.includes(token.variable)) return formatDateValue(token, value, warnings);
  if (LIST_VARIABLES.includes(token.variable)) return capList(token, value, warnings);
  if (token.sign) warnings.push(`${token.variable} takes no offset`);
  if (token.format != null) warnings.push(`${token.variable} takes no format`);
  return value;
}

const MALFORMED_PREFIX = "Malformed slicer token: ";

// The quote of a malformed token keeps the closing brace and stops before the next tag or
// whitespace, so HTML content does not run on to the end of the string.
const MALFORMED_QUOTE_RE = /^\{[^}<\s]*\}?/;
const MALFORMED_QUOTE_CAP = 48;

function quoteMalformedToken(stripped, index) {
  const quoted = MALFORMED_QUOTE_RE.exec(stripped.slice(index))[0];
  if (quoted.length <= MALFORMED_QUOTE_CAP) return quoted;
  return `${quoted.slice(0, MALFORMED_QUOTE_CAP - 1)}…`;
}

/**
 * True for a brace-scan warning. A field hides these while its `{Slicer.` menu is open,
 * because the coach is in the middle of the token.
 * @param {string} warning
 */
export function isMalformedSlicerWarning(warning) {
  return typeof warning === "string" && warning.startsWith(MALFORMED_PREFIX);
}

/** The brace scan over the text with every well-formed token removed. */
function warnMalformedSlicerTokens(text, tokens, warnings) {
  let stripped = "";
  let cursor = 0;
  for (const token of tokens) {
    stripped += text.slice(cursor, token.start);
    cursor = token.end;
  }
  stripped += text.slice(cursor);
  for (const match of stripped.matchAll(BRACE_RE)) {
    if (!/^slicer/i.test(match[1])) continue;
    warnings.push(`${MALFORMED_PREFIX}${quoteMalformedToken(stripped, match.index)}`);
  }
}

/**
 * Resolve every token in a title or text template.
 *
 * `values` maps each slicer key to `{ kind, label, values }`, where `values` holds every
 * variable the slicer offers and null for one that is not available yet (a session
 * window has no calendar start before the engine answers). Leave `values` undefined
 * when the caller has no board, and slicer tokens stay literal without a warning.
 *
 * `options.now` is the clock for the `{CurrentDate}` family; `options.escapeHtml` escapes
 * every substituted value and is on for text content, off for a title.
 */
export function resolveTemplate(template, values, options) {
  const text = typeof template === "string" ? template : "";
  const warnings = [];
  const escape = options?.escapeHtml ? escapeHtml : (s) => s;
  const now = options?.now ?? new Date();
  const tokens = parseTokens(text);

  let resolved = "";
  let cursor = 0;
  for (const token of tokens) {
    const value = token.prefixed
      ? resolveSlicerToken(token, values, warnings)
      : resolveCurrentToken(token, now, warnings);
    resolved += text.slice(cursor, token.start) + (value == null ? token.raw : escape(value));
    cursor = token.end;
  }
  resolved += text.slice(cursor);

  warnMalformedSlicerTokens(text, tokens, warnings);
  return { resolved, warnings };
}

const SAVE_BLOCKING_WARNING_RE =
  /^(Unknown variable: |No slicer has key |Unknown slicer variable: |Malformed slicer token: |\S+ is a (?:date|subject) slicer and has no )/;

/**
 * The warnings the backend also rejects at save time. A configurator disables Save while
 * this list is not empty; a format or offset warning renders the value and does not block.
 */
export function saveBlockingWarnings(template, values) {
  return resolveTemplate(template, values).warnings.filter((w) => SAVE_BLOCKING_WARNING_RE.test(w));
}
